// Conector del puente legacy → `/hub/tpv` (ver legacyBridgeCore.js).
//
//  - `shouldBridge(url)`: con V2 activo, los hubs migrados no abren socket.
//  - `registerBridge(url, functions)`: registra los listeners del hook legacy
//    en el bus y garantiza UNA conexión `/hub/tpv` (la misma URL que
//    useTpvRealtime en el TPV → el HubConnectionManager la comparte).
//  - Personal (JWT de restaurante): `/hub/tpv?deviceId=…`; las sesiones que
//    pidan los hooks (carta del camarero) se siguen con `WatchSession`.
//  - Comensal QR (token de sesión): `/hub/tpv?sessionId=…`.
import buildHubUrl from "src/utils/hubs/buildHubUrl";
import {
  acquireConnection,
  releaseConnection,
  addReconnectListener,
} from "src/utils/hubs/HubConnectionManager";
import { getDeviceId } from "src/utils/deviceId";
import { isRealtimeV2Enabled } from "./realtimeFlag";
import { createLegacyBus, isBridgedHub, mapEnvelopeToLegacy, parseHubUrl } from "./legacyBridgeCore";

export const TPV_HUB_PATH = "/hub/tpv";

// Se decide una vez por carga (el kill-switch requiere recargar).
const V2 = isRealtimeV2Enabled();

const bus = createLegacyBus();
// url de /hub/tpv → { count, connection, operator, unsubscribeReconnect, watched: Map<sid, count> }
const connections = new Map();

function getStore() {
  // Import perezoso: evita el ciclo store ↔ hubs en el arranque.
  // eslint-disable-next-line global-require
  return require("src/redux/store").store;
}

function getAxios() {
  // eslint-disable-next-line global-require
  return require("src/utils/axios").default;
}

export function shouldBridge(url) {
  if (!V2) return false;
  const info = parseHubUrl(url);
  return !!info && isBridgedHub(info.hub);
}

/** Personal (TPV/panel/KDS) o comensal QR, según la sesión del navegador. */
function resolveV2Url(info) {
  let state = null;
  try {
    state = getStore()?.getState?.();
  } catch {
    state = null;
  }
  const auth = state?.authJwt;
  // El comensal QR también tiene `authJwt.user` (StartSession guarda ahí el
  // usuario público del restaurante para pintar la carta) pero no es personal:
  // sin login de restaurante y con sesión QR va por `?sessionId=`. Si se le
  // trataba como personal, WatchSession devolvía false y no le llegaba ningún
  // evento de su sesión (aprobación del pedido, cuenta…) hasta recargar.
  const isQrCustomer = !auth?.isAuthenticated && !!auth?.session?.id;
  if (auth?.user?.id && !isQrCustomer) {
    return { url: buildHubUrl(TPV_HUB_PATH, { deviceId: getDeviceId() }), staff: true };
  }
  // La sesión del store primero: todos los hooks QR (sesión, avisos, productos)
  // comparten así la MISMA conexión aunque alguno no pase sessionId en su URL.
  const sessionId = state?.authJwt?.session?.id || info?.sessionId || null;
  return { url: buildHubUrl(TPV_HUB_PATH, { sessionId }), staff: false };
}

async function deliver(env) {
  const targets = mapEnvelopeToLegacy(env);
  for (const t of targets) {
    if (!bus.has(t.hub, t.event)) continue;
    if (t.argsFrom === "user") {
      // V2 no envía la licencia (datos sensibles): se pide como hacía el
      // backend legacy antes de emitir `user/{id}`.
      try {
        const res = await getAxios().get(`authentication/public/user/${env.restauranteId}`);
        const user = res?.data?.user;
        if (user) bus.emit(t.hub, t.event, [user]);
      } catch {
        // Aviso informativo: el siguiente refresco lo recoge.
      }
      continue;
    }
    bus.emit(t.hub, t.event, t.args || []);
  }
}

// Reenvío (`GET realtime/sync`) tras un hueco: los eventos repuestos
// también tienen que llegar a los hooks legacy puenteados. Un evento puede
// haber llegado ya en directo (llegó N+2 antes que N+1 y el reenvío trae
// ambos): se recuerdan los últimos seq entregados para no duplicar.
const DELIVERED_MAX = 4000;
const delivered = new Set();
const deliveredOrder = [];

function deliveredKey(env) {
  if (!env || typeof env.seq !== "number" || env.seq <= 0) return null;
  return `${env.epoch || ""}:${env.restauranteId || ""}:${env.seq}`;
}

function deliverOnce(env) {
  const key = deliveredKey(env);
  if (key) {
    if (delivered.has(key)) return Promise.resolve();
    delivered.add(key);
    deliveredOrder.push(key);
    if (deliveredOrder.length > DELIVERED_MAX) delivered.delete(deliveredOrder.shift());
  }
  return deliver(env);
}

/** Entrega a los consumidores legacy un evento repuesto por `realtime/sync`. */
export function deliverReplayed(env) {
  if (!V2) return Promise.resolve();
  return deliverOnce(env);
}

function watch(entry, sessionId) {
  const conn = entry.connection;
  if (!conn || !sessionId || !entry.staff) return;
  if (conn.state !== "Connected") return;
  conn.invoke("WatchSession", sessionId).catch(() => {});
}

function unwatch(entry, sessionId) {
  const conn = entry.connection;
  if (!conn || !sessionId || !entry.staff) return;
  if (conn.state !== "Connected") return;
  conn.invoke("UnwatchSession", sessionId).catch(() => {});
}

function acquireV2(info) {
  const { url, staff } = resolveV2Url(info);
  let entry = connections.get(url);
  if (!entry) {
    const { connection, startPromise } = acquireConnection(url);
    const operator = (env) => { deliverOnce(env); };
    connection.on("evt", operator);
    entry = { url, staff, count: 0, connection, operator, watched: new Map() };
    const rewatchAll = () => entry.watched.forEach((_, sid) => watch(entry, sid));
    startPromise.then(rewatchAll).catch(() => {});
    entry.unsubscribeReconnect = addReconnectListener(url, rewatchAll);
    connections.set(url, entry);
  }
  entry.count += 1;
  return entry;
}

function releaseV2(entry) {
  entry.count -= 1;
  if (entry.count > 0) return;
  try {
    entry.connection.off("evt", entry.operator);
  } catch {
    // ya cerrada
  }
  entry.unsubscribeReconnect?.();
  connections.delete(entry.url);
  releaseConnection(entry.url);
}

/**
 * Sustituto de CreateConnection para hubs migrados. Devuelve el cleanup.
 */
export function registerBridge(url, functions = []) {
  const info = parseHubUrl(url);
  const offs = functions.map((f) => bus.on(info.hub, f.id, f.operator));
  const entry = acquireV2(info);

  // Hooks de sesión en el TPV (carta del camarero): seguir esa sesión.
  const sid = info.sessionId;
  if (sid && entry.staff) {
    const n = entry.watched.get(sid) || 0;
    entry.watched.set(sid, n + 1);
    if (n === 0) watch(entry, sid);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    offs.forEach((off) => off());
    if (sid && entry.staff) {
      const n = (entry.watched.get(sid) || 1) - 1;
      if (n <= 0) {
        entry.watched.delete(sid);
        unwatch(entry, sid);
      } else {
        entry.watched.set(sid, n);
      }
    }
    releaseV2(entry);
  };
}

// Para tests / diagnóstico.
export const __bridgeInternals = { bus, connections, deliver, deliverOnce, delivered };
